import { AfterViewInit, Component, effect, inject, Signal, ChangeDetectorRef, untracked } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AupMapLayer, SelectedMapLayer, AupGeoJSONOptions, MapLayerTypeEnum } from 'src/app/model/map.model';
import { AccordionModule } from 'primeng/accordion';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { AupMapService } from '../services/aup-map.service';
import { MapToolbarComponent } from 'src/app/map/map-toolbar/map-toolbar.component';
import { AupReservation, AupReservationFilters, SelectedTableRow } from 'src/app/model/observer.model';
import { MapTimeComponent } from 'src/app/map/map-time/map-time.component';
import { MapHeightComponent } from 'src/app/map/map-height/map-height.component';
import { ObserverService } from 'src/app/services/observer.service';
import * as Turf from '@turf/turf';
import * as L from 'leaflet';
import { layersStyle } from 'src/app/map/layersStyle';
import { MapToolbarService } from 'src/app/map/services/map-toolbar.service';
import { MapToolbarAction, MapToolbarPayload } from 'src/app/map/map-toolbar/map-toolbar-actions';
import { SelectedMapLayersComponent } from './selected-map-layers/selected-map-layers.component';
import { StyleType } from 'src/app/model/layers.model';
import { getAltitudeIndex } from 'src/app/model/altitudes.model';
import { MapHighlightService } from 'src/app/map/services/map-highlight.service';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { MapOperationsService } from 'src/app/map/services/map-operations.service';
import { MapLayerService } from 'src/app/map/services/map-layer.service';
import { LayoutUtilsService } from 'src/app/services/layout-utils.service';
import { environment } from 'src/environments/environment';
import { normalizeFromAupTime } from 'src/app/shared/utils/time.utils';

@Component({
  selector: 'rqa-aup-map',
  templateUrl: './aup-map.component.html',
  styleUrls: ['./aup-map.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    LeafletModule,
    AccordionModule,
    TooltipModule,
    ButtonModule,
    MapToolbarComponent,
    AccordionModule,
    MapTimeComponent,
    MapHeightComponent,
    SelectedMapLayersComponent
  ]
})
export class AupMapComponent implements AfterViewInit {
  private aupMapService = inject(AupMapService);
  private observerService = inject(ObserverService);
  private toolbarService = inject(MapToolbarService);
  private mapHighlightService = inject(MapHighlightService);
  private cdr = inject(ChangeDetectorRef);
  private mapOperationsService = inject(MapOperationsService);
  private mapLayerService = inject(MapLayerService);
  private layoutUtilsService = inject(LayoutUtilsService);

  readonly aupMapLayers: Signal<AupMapLayer[]> = this.aupMapService.mapLayers;
  readonly filters: Signal<AupReservationFilters> = this.observerService.filtersSignal;
  readonly toolbarAction: Signal<{ action: MapToolbarAction; payload?: MapToolbarPayload } | null> =
    this.toolbarService.actionSignal;
  readonly selectedRow: Signal<SelectedTableRow | null> = this.observerService.selectedRow;

  // Map-related signals from services
  readonly currentMarker = this.mapOperationsService.currentMarker;
  readonly groupedLayers = this.mapLayerService.groupedLayers;
  readonly filterLayerTypes = this.mapLayerService.filterLayerTypes;
  readonly showFilters = this.mapLayerService.showFilters;
  readonly disabledAreas = this.mapLayerService.disabledAreas;
  readonly contentHeight = this.layoutUtilsService.contentHeight;

  activeAccordionIndex: number = 0;
  timeRange: string[] = [];
  heightRange: string[] = [];
  layerStyles = layersStyle;
  lastClickedLayer: L.GeoJSON | null = null;
  layersContainingPoint: SelectedMapLayer[] = [];
  fetchLayersCount = 0;
  disabledFilters = false;

  private isMapReady = false;

  // Map options
  options: L.MapOptions = {
    layers: [L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: 'Open Street Map' })],
    zoom: 6,
    minZoom: 5,
    maxZoom: 17,
    center: new L.LatLng(environment.center[0], environment.center[1]),
    zoomControl: false
  };

  constructor() {
    // Effect only for AUP layers
    effect(() => {
      const layers = this.aupMapLayers();

      if (this.isMapReady && layers.length > 0) {
        untracked(() => {
          this.handleLayersChanged(layers);
        });
      }
    });

    // Effect only for filters
    effect(() => {
      const filters = this.filters();
      if (filters) {
        untracked(() => {
          this.timeRange = [filters.from, filters.until];
          this.heightRange = [filters.flMin, filters.flMax];
        });
      }
    });

    // Effect only for selected row
    effect(() => {
      const selectedRow = this.selectedRow();
      untracked(() => {
        if (selectedRow) {
          this.layersContainingPoint = [];
          this.higlightSelectedLayer(selectedRow.rowData.designator);
          if (selectedRow.isVisualizationChecked) {
            this.timeRange = [selectedRow.rowData.from, selectedRow.rowData.until];
            this.heightRange = [selectedRow.rowData.flMin, selectedRow.rowData.flMax];
            this.disabledFilters = true;
          } else {
            const filters = this.filters();
            if (filters) {
              this.timeRange = [filters.from, filters.until];
              this.heightRange = [filters.flMin, filters.flMax];
            }
            this.disabledFilters = false;
          }
        } else {
          this.disabledFilters = false;
        }
      });
    });

    // Effect only for toolbar actions
    effect(() => {
      const action = this.toolbarAction();
      if (action) {
        untracked(() => {
          this.handleToolbarAction(action);
          this.toolbarService.clearAction();
        });
      }
    });

    // Effect only for highlight service
    effect(() => {
      const designator = this.mapHighlightService.highlightedDesignator();
      untracked(() => {
        this.bringToFrontLayerAndHighlight(designator);
      });
    });

    // Effect only for map resize
    effect(() => {
      const mapResize = this.mapOperationsService.resizeMap();
      if (mapResize && this.isMapReady) {
        untracked(() => {
          setTimeout(() => {
            const map = this.mapOperationsService.mapInstance();
            if (map) {
              map.invalidateSize(true);
            }
          }, 300);
        });
      }
    });
  }

  onMapReady(map: L.Map): void {
    this.mapOperationsService.setMapInstance(map);

    setTimeout(() => {
      if (map && map.getContainer()) {
        try {
          map.invalidateSize(true);
          this.isMapReady = true;

          const layers = this.aupMapLayers();
          if (layers.length > 0) {
            this.handleLayersChanged(layers);
          }
        } catch (error) {
          console.warn('Failed to initialize AUP map:', error);
        }
      }
    }, 300);
  }

  private handleLayersChanged(layers: AupMapLayer[]): void {
    this.createAupLayers(layers);
    this.fetchLayersCount++;
  }

  ngAfterViewInit(): void {
    this.toolbarService.clearAction();
    setTimeout(() => {
      const map = this.mapOperationsService.mapInstance();
      if (map) {
        map.on('drag', () => {
          this.layersContainingPoint = [];
          this.cdr.detectChanges();
        });
      }
    }, 100);
  }

  onMapClick(e: L.LeafletMouseEvent): void {
    this.findIntersectingLayers(e.latlng);
  }

  onTimeRangeChange(timeRange: string[]): void {
    const filters = {
      ...this.filters(),
      from: timeRange[0],
      until: timeRange[1]
    };
    this.observerService.updateFilters(filters);
  }

  onHeightRangeChange(heightRange: string[]): void {
    const filters = {
      ...this.filters(),
      flMin: heightRange[0],
      flMax: heightRange[1]
    };
    this.observerService.updateFilters(filters);
  }

  zoomIn(): void {
    this.mapOperationsService.zoomIn();
  }

  zoomOut(): void {
    this.mapOperationsService.zoomOut();
  }

  async onSearch(): Promise<void> {
    const coordinates = await this.mapOperationsService.openSearchDialog();
    if (coordinates) {
      this.mapOperationsService.addSearchMarker(coordinates);
    }
  }

  toggleFilters(): void {
    this.mapLayerService.toggleFilters();
  }

  private clearAllLayers(): void {
    const map = this.mapOperationsService.mapInstance();
    if (map) {
      this.mapLayerService.clearAllLayers(map);
    }
  }

  createAupLayers(layers: AupMapLayer[]): void {
    this.layersContainingPoint = [];
    const groupedLayers: { [key: string]: L.LayerGroup } = {};
    this.clearAllLayers();
    const map = this.mapOperationsService.mapInstance();
    if (!map) return;

    layers.forEach((layerData) => {
      const {
        designator,
        type,
        geometry,
        reservations,
        flMin,
        flMax,
        displayFlMax,
        displayFlMin,
        styles,
        multipleAltitude
      } = layerData;
      if (!groupedLayers[type]) {
        groupedLayers[type] = L.layerGroup().addTo(map);
      }
      const resultGeometry = this.mapLayerService.convertGeometry(geometry);
      const layer = new L.GeoJSON(
        resultGeometry as Turf.GeometryObject,
        {
          designator,
          reservations,
          type,
          flMin,
          flMax,
          displayFlMax,
          displayFlMin,
          styles,
          multipleAltitude,
          className: designator
        } as AupGeoJSONOptions
      );

      if (layer) {
        groupedLayers[type].addLayer(layer);
        this.mapLayerService.setLayerStyles(layer, styles, StyleType.ACTIVATED);
        layer.on('click', () => this.onLayerClick(layer));
      }
    });

    this.mapLayerService.setGroupedLayers(groupedLayers);

    if (this.fetchLayersCount === 0) {
      const filterTypes = Object.keys(groupedLayers).map((type) => {
        return {
          type: type,
          visible: type === MapLayerTypeEnum.AUP || type === MapLayerTypeEnum.UUP,
          checked: type === MapLayerTypeEnum.AUP || type === MapLayerTypeEnum.UUP
        };
      });
      this.mapLayerService.setFilterLayerTypes(filterTypes);
    }

    const filterTypes = this.mapLayerService.filterLayerTypes();
    filterTypes.forEach((el) => {
      this.mapLayerService.toggleLayerGroupVisibility(el.checked, el.type, map);
    });
  }

  onLayerClick(layer: L.GeoJSON): void {
    const map = this.mapOperationsService.mapInstance();
    if (map && this.isMapReady && map.getContainer()) {
      try {
        map.fitBounds(layer.getBounds(), { maxZoom: 8 });
      } catch (error) {
        console.warn('Failed to fit bounds:', error);
      }
    }
    this.mapLayerService.resetLayerStyles();

    const styles = (layer.options as AupGeoJSONOptions).styles;
    this.mapLayerService.setLayerStyles(layer, styles, StyleType.HIGHLIGHT);
    layer.bringToFront();
  }

  private handleToolbarAction(event: { action: MapToolbarAction; payload?: MapToolbarPayload } | null): void {
    if (event) {
      this.layersContainingPoint = [];
      switch (event.action) {
        case 'zoomIn':
          this.mapOperationsService.zoomIn();
          break;
        case 'zoomOut':
          this.mapOperationsService.zoomOut();
          break;
        case 'search':
          this.onSearch();
          break;
        case 'toggleLayerGroup':
          if (event.payload) {
            const map = this.mapOperationsService.mapInstance();
            if (map) {
              this.mapLayerService.toggleLayerGroupVisibility(event.payload.visible, event.payload.groupId, map);
              this.mapLayerService.bringToFrontLayers([MapLayerTypeEnum.AUP, MapLayerTypeEnum.UUP]);
            }
          }
          break;
        case 'filters': {
          this.mapLayerService.toggleFilters();
          const showFilters = this.mapLayerService.showFilters();
          this.mapLayerService.moveCoordinatesDiv(showFilters);
          break;
        }
      }
    }
  }

  private findIntersectingLayers(latlng: L.LatLng): void {
    this.layersContainingPoint = [];
    const map = this.mapOperationsService.mapInstance();
    if (!map) return;

    map.eachLayer((layer) => {
      if (map.hasLayer(layer)) {
        if (this.mapOperationsService.isPointInLayer(latlng, layer as L.GeoJSON)) {
          const selectedLayer = this.mapSelectedLayer(layer as L.GeoJSON);
          this.layersContainingPoint.push(selectedLayer);
        }
      }
    });

    const convertedDoubleDesignators = this.layersContainingPoint
      .sort((a, b) => {
        return a.designator.localeCompare(b.designator);
      })
      .reduce<{ [key: string]: SelectedMapLayer }>((acc, item) => {
        const { designator, children, showChildren, flMinMax, multipleAltitude, type } = item;
        if (!acc[designator]) {
          acc[designator] = {
            designator,
            type,
            children: [],
            showChildren,
            flMinMax,
            multipleAltitude
          };
        }
        acc[designator].children.push(...children);
        return acc;
      }, {});

    this.layersContainingPoint = Object.values(convertedDoubleDesignators);
    if (!this.layersContainingPoint.length) {
      this.mapOperationsService.addMarker(latlng);
    } else {
      const currentMarker = this.mapOperationsService.currentMarker();
      const map = this.mapOperationsService.mapInstance();
      if (currentMarker && map) {
        map.removeLayer(currentMarker);
      }
    }
  }

  private mapSelectedLayer(layer: L.GeoJSON): SelectedMapLayer {
    const layerOptions = layer.options as AupGeoJSONOptions;
    return {
      designator: `${layerOptions.designator}`,
      type: `${layerOptions.type}`,
      flMinMax: `${layerOptions.displayFlMin}-${layerOptions.displayFlMax}`,
      children: layerOptions?.reservations.length ? this.mapReservationsToChildren(layerOptions.reservations) : [],
      multipleAltitude: layerOptions.multipleAltitude,
      showChildren: false
    };
  }

  private mapReservationsToChildren(reservations: AupReservation[]): string[] {
    return reservations
      .sort((a, b) => {
        const aFromNormalized = normalizeFromAupTime(a.from);
        const bFromNormalized = normalizeFromAupTime(b.from);

        if (aFromNormalized !== bFromNormalized) {
          return aFromNormalized - bFromNormalized;
        }

        const altitudeDiff = getAltitudeIndex(a.flMin) - getAltitudeIndex(b.flMin);
        if (altitudeDiff !== 0) {
          return altitudeDiff;
        }

        return a.responsibleUnit.localeCompare(b.responsibleUnit);
      })
      .map((res) => `${res.flMin}-${res.flMax} ${res.from}- ${res.until} ${res.responsibleUnit}`);
  }

  private higlightSelectedLayer(designator: string | null): void {
    if (designator) {
      this.mapLayerService.resetLayerStyles();
      this.mapLayerService.forEachLayer((layer) => {
        const options = layer.options as AupGeoJSONOptions;
        if (options.designator === designator) {
          (layer as L.GeoJSON).bringToFront();
          const styles = (layer.options as AupGeoJSONOptions).styles;
          this.mapLayerService.setLayerStyles(layer as L.GeoJSON, styles, StyleType.HIGHLIGHT);
        } else {
          const styles = (layer.options as AupGeoJSONOptions).styles;
          this.mapLayerService.setLayerStyles(layer as L.GeoJSON, styles, StyleType.TRANSPARENT);
        }
      });
    }
  }

  private bringToFrontLayerAndHighlight(designator: string | null): void {
    this.mapLayerService.forEachLayer((layer) => {
      const options = layer.options as AupGeoJSONOptions;
      if (options.designator === designator) {
        (layer as L.GeoJSON).bringToFront();
        const styles = (layer.options as AupGeoJSONOptions).styles;
        this.mapLayerService.setLayerStyles(layer as L.GeoJSON, styles, StyleType.HIGHLIGHT);
      } else {
        const styles = (layer.options as AupGeoJSONOptions).styles;
        this.mapLayerService.setLayerStyles(layer as L.GeoJSON, styles, StyleType.ACTIVATED);
      }
    });
  }
}
